import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useDeveloperAccess } from '@/hooks/useDeveloperAccess';
import Loader from '../components/Loader';

type DeveloperGuardProps = {
    children: ReactNode;
};

/**
 * DeveloperGuard
 *
 * Protects routes that should only be accessible to developers.
 * Checks if the user's email is in the Firestore `config/developerAccess` document.
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="admin/cms"
 *   element={
 *     <DeveloperGuard>
 *       <CMSManagementPage />
 *     </DeveloperGuard>
 *   }
 * />
 * ```
 */
export default function DeveloperGuard({ children }: DeveloperGuardProps) {
    const { user, loading: authLoading } = useAuth();
    const { isDeveloper, isLoading: developerLoading } = useDeveloperAccess();
    const location = useLocation();

    // Show loader while checking auth or developer access
    if (authLoading || developerLoading) {
        return (
            <div className="relative min-h-screen bg-bg-primary">
                <Loader variant="overlay" message="Checking access…" />
            </div>
        );
    }

    // Not logged in - redirect to login
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Not a developer - redirect to unauthorized
    if (!isDeveloper) {
        return <Navigate to="/unauthorized" replace />;
    }

    return <>{children}</>;
}
